import { axiosInstance } from '@/rest/axios'
import { userPermissionsApi } from '@/rest/user-permissions'

// Mirrors the v2-dashboard BFF's account-features-status route: it aggregates
// 3 backend calls rather than being a single upstream endpoint.
// See: v2-dashboard/src/app/api/trader-profile/[profileId]/account-features-status/route.ts

const toCamelCase = (key) =>
  key.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase())

const camelizeShallow = (obj) => {
  if (!obj || typeof obj !== 'object') return obj
  return Object.keys(obj).reduce((acc, key) => {
    acc[toCamelCase(key)] = obj[key]
    return acc
  }, {})
}

const parseOptionLevel = (value) => {
  if (!value) return null
  const match = String(value).match(/\d+/)
  return match ? parseInt(match[0], 10) : null
}

const computeMarginStatus = (marginData, userPermissions) => {
  if (!marginData || !marginData.margin_requested) return 'not_requested'
  // A pending review outranks the permission flag: the request is still live
  // even though trading has not been granted yet.
  if (marginData.margin_status === 'NEEDS_REVIEW') return 'in_review'
  if (marginData.margin_status === 'APPROVED') {
    // Approved, but permissions are the source of truth for what the account
    // can actually do — a revoked permission reads as disabled.
    return userPermissions?.margin === true ? 'enabled' : 'disabled'
  }
  return 'not_requested'
}

const computeOptionsStatus = (optionData, userPermissions) => {
  if (!optionData) {
    return {
      status: 'not_approved',
      approvedLevel: null,
      requestedLevel: null,
      canUpdateLevel: false,
    }
  }

  const approvedLevel = parseOptionLevel(optionData.approved_option_level)
  const requestedLevel = parseOptionLevel(optionData.requested_option_level)
  const levelsMatch = approvedLevel === requestedLevel

  if (optionData.approval_status === 'NEEDS_REVIEW' && !levelsMatch) {
    return {
      status: 'in_review',
      approvedLevel,
      requestedLevel,
      canUpdateLevel: false,
    }
  }

  if (optionData.approval_status !== 'APPROVED') {
    return {
      status: 'not_approved',
      approvedLevel,
      requestedLevel,
      canUpdateLevel: false,
    }
  }

  // APPROVED — mirror margin and defer to permissions for what the account can
  // actually do. A revoked permission also means the level can't be changed.
  if (userPermissions?.optionsTrading !== true) {
    return {
      status: 'disabled',
      approvedLevel,
      requestedLevel,
      canUpdateLevel: false,
    }
  }

  return {
    status: 'enabled',
    approvedLevel,
    requestedLevel,
    canUpdateLevel: levelsMatch,
  }
}

export const userProfileApi = {
  async getAccountFeaturesStatus(profile_id, account_number) {
    const [marginResult, optionResult, permissionsResult] =
      await Promise.allSettled([
        axiosInstance.get(`v2/user_profile/${profile_id}/margin`),
        axiosInstance.get(`v2/user_profile/${profile_id}/option_level`),
        userPermissionsApi.getUserPermissionsByAccountNumber(account_number),
      ])

    // Treat failures (e.g. 404 when a feature was never requested) as null
    const marginData =
      marginResult.status === 'fulfilled' ? marginResult.value : null
    const optionData =
      optionResult.status === 'fulfilled' ? optionResult.value : null
    const userPermissions =
      permissionsResult.status === 'fulfilled'
        ? camelizeShallow(permissionsResult.value)
        : null

    const optionFormData = optionData
      ? {
          stockTradesPerYear: optionData.stock_trades_per_year ?? 0,
          optionsTradesPerYear: optionData.options_trades_per_year ?? 0,
          commoditiesTradesPerYear: optionData.commodities_trades_per_year ?? 0,
          corporateBondsTradesPerYear:
            optionData.corporate_bonds_trades_per_year ?? 0,
          governmentBondsTradesPerYear:
            optionData.government_bonds_trades_per_year ?? 0,
          municipalBondsTradesPerYear:
            optionData.municipal_bonds_trades_per_year ?? 0,
          fundsTradesPerYear: optionData.funds_trades_per_year ?? 0,
          marginAccountTradesPerYear:
            optionData.margin_account_trades_per_year ?? 0,
        }
      : null

    return {
      margin: { status: computeMarginStatus(marginData, userPermissions) },
      options: computeOptionsStatus(optionData, userPermissions),
      userPermissions,
      optionFormData,
    }
  },
  getBeneficiaries: (profile_id) =>
    axiosInstance.get(`v2/user_profile/${profile_id}/beneficiaries`),
  getRelatedContacts: (profile_id) =>
    axiosInstance.get(`v2/user_profile/${profile_id}/related_contacts`),
  updateRelatedContacts: (profile_id, contact_id, body, params = {}) =>
    axiosInstance.put(
      `v2/user_profile/${profile_id}/related_contacts/${contact_id}`,
      body,
      { params }
    ),
  getSuitabilityProfile: (profile_id) =>
    axiosInstance.get(`v2/user_profile/${profile_id}/suitability_profile`),
  updateSuitabilityProfile: (profile_id, body) =>
    axiosInstance.patch(
      `v2/user_profile/${profile_id}/suitability_profile`,
      body
    ),
  getPersonalInfo: (profile_id) =>
    axiosInstance.get(`v2/user_profile/${profile_id}/personal_info`),
  // v2-dashboard exposes this as the `entity_info` BFF section, which maps to
  // the backend's `entity_account_info` endpoint.
  // See: v2-dashboard/src/app/api/trader-profile/[profileId]/[...section]/route.ts
  getEntityAccountInfo: (profile_id) =>
    axiosInstance.get(`v2/user_profile/${profile_id}/entity_account_info`),
  // Entity accounts update here, not personal_info: the business phone and
  // address live on this section as businessTelephoneNumber/businessAddress.
  updateEntityAccountInfo: (profile_id, body) =>
    axiosInstance.patch(
      `v2/user_profile/${profile_id}/entity_account_info`,
      body
    ),
  getSsn: (profile_id) =>
    axiosInstance.get(`v2/back_office/user_profile/${profile_id}/ssn`),
  getHomeAddress: (profile_id) =>
    axiosInstance.get(`v2/user_profile/${profile_id}/home_address`),
  getMailingAddress: (profile_id) =>
    axiosInstance.get(`v2/user_profile/${profile_id}/mailing_address`),
  updatePersonalInfo: (profile_id, body) =>
    axiosInstance.patch(`v2/user_profile/${profile_id}/personal_info`, body),
  updateHomeAddress: (profile_id, body) =>
    axiosInstance.patch(`v2/user_profile/${profile_id}/home_address`, body),
  updateMailingAddress: (profile_id, body) =>
    axiosInstance.patch(`v2/user_profile/${profile_id}/mailing_address`, body),
  getTrustedContact: (profile_id) =>
    axiosInstance.get(`v2/user_profile/${profile_id}/trusted_contact`),
  upsertTrustedContact: (profile_id, body) =>
    axiosInstance.put(`v2/user_profile/${profile_id}/trusted_contact`, body),
  getFinancialProfile: (profile_id) =>
    axiosInstance.get(`v2/user_profile/${profile_id}/financial_profile`),
  updateFinancialProfile: (profile_id, body) =>
    axiosInstance.patch(
      `v2/user_profile/${profile_id}/financial_profile`,
      body
    ),
  getOptionLevel: (profile_id) =>
    axiosInstance.get(`v2/user_profile/${profile_id}/option_level`),
  requestOptionLevel: (profile_id, body) =>
    axiosInstance.post(`v2/user_profile/${profile_id}/option_level`, body),
  approveOptionLevel: (form_id, body) =>
    axiosInstance.post(
      `v2/user_profile/forms/${form_id}/option_level/approval`,
      body
    ),
  getMargin: (profile_id) =>
    axiosInstance.get(`v2/user_profile/${profile_id}/margin`),
  // Mirrors v2-dashboard's `POST /api/trader-profile/agreements` with
  // type: 'margin' - the only field the backend accepts is the disclosure date.
  // See: v2-dashboard/src/app/api/trader-profile/agreements/route.ts
  requestMargin: (profile_id, body) =>
    axiosInstance.post(`v2/user_profile/${profile_id}/margin`, body),
}
